import React, { useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { Tooltip } from 'react-tooltip';
import { X, ZoomIn, ZoomOut, Home, DollarSign, TrendingUp, BarChart3, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';

const US_COUNTIES_TOPO = "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

const STATE_FIPS = {
  'AL':'01','AK':'02','AZ':'04','AR':'05','CA':'06','CO':'08','CT':'09','DE':'10',
  'FL':'12','GA':'13','HI':'15','ID':'16','IL':'17','IN':'18','IA':'19','KS':'20',
  'KY':'21','LA':'22','ME':'23','MD':'24','MA':'25','MI':'26','MN':'27','MS':'28',
  'MO':'29','MT':'30','NE':'31','NV':'32','NH':'33','NJ':'34','NM':'35','NY':'36',
  'NC':'37','ND':'38','OH':'39','OK':'40','OR':'41','PA':'42','RI':'44','SC':'45',
  'SD':'46','TN':'47','TX':'48','UT':'49','VT':'50','VA':'51','WA':'53','WV':'54',
  'WI':'55','WY':'56','DC':'11'
};

const METRICS = [
  { key: 'gross_revenue', label: 'Gross Revenue', format: 'currency' },
  { key: 'net_revenue', label: 'Net Revenue', format: 'currency' },
  { key: 'scenario_count', label: '# Proposals', format: 'number' },
];

const fc = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

function getColor(ratio) {
  if (ratio <= 0) return '#e5e7eb';
  if (ratio < 0.15) return '#dcfce7';
  if (ratio < 0.3) return '#86efac';
  if (ratio < 0.5) return '#22c55e';
  if (ratio < 0.7) return '#16a34a';
  if (ratio < 0.85) return '#15803d';
  return '#166534';
}

export default function StateMapDrillDown({ 
  stateCode, 
  stateName,
  municipalityData, 
  onClose 
}) {
  const [tooltipContent, setTooltipContent] = useState('');
  const [metric, setMetric] = useState('gross_revenue');
  const [sortKey, setSortKey] = useState('gross_revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedMuni, setExpandedMuni] = useState(null);

  const stateFips = STATE_FIPS[stateCode];
  const municipalities = municipalityData?.municipalities || [];
  const stateTotals = municipalityData?.state_totals || {};

  // State-specific center coordinates and zoom for geoAlbersUsa projection
  const stateConfig = useMemo(() => {
    const STATE_CENTERS = {
      'AL': [-86.9, 32.8, 5], 'AK': [-153, 64, 3], 'AZ': [-111.1, 34, 4.5],
      'AR': [-92.2, 34.8, 5.5], 'CA': [-119.4, 36.8, 3.5], 'CO': [-105.5, 39, 5],
      'CT': [-72.7, 41.6, 12], 'DE': [-75.5, 39, 12], 'FL': [-81.5, 27.8, 4],
      'GA': [-83.5, 33, 5], 'HI': [-155.5, 20, 5], 'ID': [-114.7, 44, 4.5],
      'IL': [-89.0, 40, 5], 'IN': [-86.1, 39.8, 6], 'IA': [-93.1, 42, 5.5],
      'KS': [-98.5, 38.5, 5], 'KY': [-85.8, 37.8, 5.5], 'LA': [-91.9, 31.2, 5.5],
      'ME': [-69.4, 45.3, 5.5], 'MD': [-76.6, 39, 8], 'MA': [-71.8, 42.3, 10],
      'MI': [-84.5, 44.3, 4.5], 'MN': [-94.7, 46.4, 4.5], 'MS': [-89.7, 32.7, 5.5],
      'MO': [-91.8, 38.6, 5], 'MT': [-109.5, 47, 4.5], 'NE': [-99.9, 41.5, 5],
      'NV': [-116.7, 38.8, 4], 'NH': [-71.6, 43.2, 8], 'NJ': [-74.4, 40.1, 8],
      'NM': [-105.9, 34.5, 4.5], 'NY': [-75.5, 43, 4.5], 'NC': [-79.0, 35.5, 5],
      'ND': [-100.5, 47.5, 5.5], 'OH': [-82.9, 40.4, 5.5], 'OK': [-97.1, 35.5, 5.5],
      'OR': [-120.6, 43.8, 4.5], 'PA': [-77.2, 41.2, 5.5], 'RI': [-71.5, 41.7, 14],
      'SC': [-81.2, 33.8, 6], 'SD': [-100, 43.9, 5.5], 'TN': [-86.6, 35.5, 5.5],
      'TX': [-99.9, 31.2, 3.5], 'UT': [-111.1, 39.3, 5], 'VT': [-72.6, 44, 8],
      'VA': [-79.4, 37.8, 5.5], 'WA': [-120.7, 47.8, 5], 'WV': [-80.5, 38.6, 6.5],
      'WI': [-89.5, 44.5, 5], 'WY': [-107.3, 43, 5.5], 'DC': [-77, 38.9, 20],
    };
    const cfg = STATE_CENTERS[stateCode] || [-96, 38, 1];
    return { center: [cfg[0], cfg[1]], zoom: cfg[2] };
  }, [stateCode]);

  const [position, setPosition] = useState({ coordinates: stateConfig.center, zoom: stateConfig.zoom });

  const maxVal = useMemo(() => {
    return Math.max(...municipalities.map(m => m[metric] || 0), 0);
  }, [municipalities, metric]);

  // Build a map of countyName → { aggregated values + all matching municipalities }.
  // A single county can contain multiple cities/towns; we aggregate so the
  // county geo gets colored even when the proposal data uses city names.
  const countyDataMap = useMemo(() => {
    const map = {};

    // Helper: derive the county "key" from a municipality entry.
    // Priority: explicit parent_county → strip "County"/"Parish" suffix from raw_name → cleaned display name.
    const countyKey = (m) => {
      if (m.parent_county) return m.parent_county.toLowerCase().trim();
      const raw = (m.raw_name || m.name || '').toLowerCase().trim();
      const stripped = raw.replace(/\s+(county|parish|borough)$/i, '').trim();
      return stripped;
    };

    municipalities.forEach(m => {
      const key = countyKey(m);
      if (!key) return;

      if (!map[key]) {
        map[key] = {
          name: key,
          gross_revenue: 0,
          net_revenue: 0,
          scenario_count: 0,
          services: new Set(),
          municipalities: [],
        };
      }
      map[key].gross_revenue += m.gross_revenue || 0;
      map[key].net_revenue += m.net_revenue || 0;
      map[key].scenario_count += m.scenario_count || 0;
      (m.services || []).forEach(s => map[key].services.add(s));
      map[key].municipalities.push(m.name || m.raw_name || '');
    });

    // Convert services Set → Array for downstream rendering.
    Object.values(map).forEach(v => { v.services = Array.from(v.services); });

    return map;
  }, [municipalities]);

  const sortedMunicipalities = useMemo(() => {
    return [...municipalities].sort((a, b) => {
      const av = a[sortKey] || 0;
      const bv = b[sortKey] || 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [municipalities, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return null;
    return sortDir === 'desc' ? <ChevronDown className="w-3 h-3 inline" /> : <ChevronUp className="w-3 h-3 inline" />;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="state-drilldown-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="drilldown-back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-white">{stateName} — County/City Drill-Down</h2>
              <p className="text-xs text-slate-300">
                {municipalities.length} locations | {fc(stateTotals.gross_revenue)} gross | {fc(stateTotals.net_revenue)} net
              </p>
            </div>
          </div>
          {/* Metric Toggle */}
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-0.5" data-testid="drilldown-metric-toggle">
            {METRICS.map(m => (
              <button
                key={m.key}
                data-testid={`drilldown-metric-${m.key}`}
                onClick={() => setMetric(m.key)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  metric === m.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-300 hover:text-white'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* County Map */}
          <div className="relative bg-slate-50 border-b border-slate-200" style={{ height: 380 }}>
            {/* Zoom Controls */}
            <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
              {[
                { icon: ZoomIn, action: () => setPosition(p => ({ ...p, zoom: Math.min(p.zoom * 1.5, 6) })) },
                { icon: ZoomOut, action: () => setPosition(p => ({ ...p, zoom: Math.max(p.zoom / 1.5, 1) })) },
                { icon: Home, action: () => setPosition({ coordinates: stateConfig.center, zoom: stateConfig.zoom }) }
              ].map(({ icon: Icon, action }, i) => (
                <button key={i} onClick={action} className="bg-white p-1.5 rounded-lg shadow-md hover:bg-slate-50 transition-colors border border-slate-200">
                  <Icon className="w-4 h-4 text-slate-600" />
                </button>
              ))}
            </div>

            {/* Legend */}
            <div className="absolute bottom-3 left-3 z-10 bg-white/90 backdrop-blur-sm p-2.5 rounded-lg shadow-md border border-slate-200">
              <p className="text-[10px] font-semibold text-slate-500 mb-1 uppercase tracking-wide">{METRICS.find(m => m.key === metric)?.label}</p>
              <div className="flex items-center gap-0.5">
                <span className="text-[9px] text-slate-400 mr-0.5">Low</span>
                {['#dcfce7','#86efac','#22c55e','#16a34a','#15803d','#166534'].map((c,i) => (
                  <div key={i} className="w-5 h-3 first:rounded-l last:rounded-r" style={{ backgroundColor: c }} />
                ))}
                <span className="text-[9px] text-slate-400 ml-0.5">High</span>
              </div>
            </div>

            <ComposableMap
              projection="geoAlbersUsa"
              projectionConfig={{ scale: 1000 }}
              width={800}
              height={500}
              style={{ width: '100%', height: '100%' }}
            >
              <ZoomableGroup zoom={position.zoom} center={position.coordinates} onMoveEnd={setPosition}>
                <Geographies geography={US_COUNTIES_TOPO}>
                  {({ geographies }) =>
                    geographies
                      .filter(geo => geo.id && geo.id.startsWith(stateFips))
                      .map(geo => {
                        const countyName = geo.properties.name.toLowerCase();
                        const cd = countyDataMap[countyName];
                        const val = cd ? (cd[metric] || 0) : 0;
                        const ratio = maxVal > 0 ? val / maxVal : 0;
                        const fill = val > 0 ? getColor(ratio) : '#e5e7eb';

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={fill}
                            stroke="#ffffff"
                            strokeWidth={0.5}
                            style={{
                              default: { outline: 'none' },
                              hover: { fill: '#4f46e5', outline: 'none', cursor: 'pointer' },
                              pressed: { outline: 'none' },
                            }}
                            onMouseEnter={() => {
                              const html = cd
                                ? `<div class="text-left min-w-[150px]">
                                    <div class="font-semibold text-slate-900">${geo.properties.name}</div>
                                    <div class="text-xs mt-1 space-y-0.5">
                                      <div>Gross: <b>${fc(cd.gross_revenue)}</b></div>
                                      <div>Net: <b>${fc(cd.net_revenue)}</b></div>
                                      <div>Proposals: <b>${cd.scenario_count}</b></div>
                                      ${cd.services?.length ? `<div>Services: ${cd.services.join(', ')}</div>` : ''}
                                    </div>
                                  </div>`
                                : `<div><div class="font-semibold">${geo.properties.name}</div><div class="text-xs text-slate-400">No data</div></div>`;
                              setTooltipContent(html);
                            }}
                            onMouseLeave={() => setTooltipContent('')}
                            data-tooltip-id="county-drilldown-tooltip"
                            data-tooltip-html={tooltipContent}
                          />
                        );
                      })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            <Tooltip
              id="county-drilldown-tooltip"
              className="z-50 !rounded-lg"
              style={{
                backgroundColor: 'white', color: '#1e293b',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                borderRadius: '0.5rem', padding: '0.75rem',
                fontSize: '0.8125rem', border: '1px solid #e2e8f0',
              }}
            />
          </div>

          {/* Municipality Data Table */}
          <div className="p-6" data-testid="municipality-table">
            <h3 className="text-base font-semibold text-slate-800 mb-3">
              Cities & Counties in {stateName}
              <span className="ml-2 text-sm font-normal text-slate-500">({municipalities.length} locations)</span>
            </h3>

            {municipalities.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <MapPinOff className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No municipality data available for this state</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200 bg-slate-50">
                      <th className="text-left py-2.5 px-3 font-semibold text-slate-600 cursor-pointer hover:text-slate-800" onClick={() => handleSort('name')}>
                        Municipality <SortIcon col="name" />
                      </th>
                      <th className="text-right py-2.5 px-3 font-semibold text-slate-600 cursor-pointer hover:text-slate-800" onClick={() => handleSort('gross_revenue')}>
                        Gross Revenue <SortIcon col="gross_revenue" />
                      </th>
                      <th className="text-right py-2.5 px-3 font-semibold text-slate-600 cursor-pointer hover:text-slate-800" onClick={() => handleSort('net_revenue')}>
                        Net Revenue <SortIcon col="net_revenue" />
                      </th>
                      <th className="text-right py-2.5 px-3 font-semibold text-slate-600 cursor-pointer hover:text-slate-800" onClick={() => handleSort('scenario_count')}>
                        Proposals <SortIcon col="scenario_count" />
                      </th>
                      <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Services</th>
                      <th className="text-center py-2.5 px-3 font-semibold text-slate-600">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMunicipalities.map((m, idx) => {
                      const isExpanded = expandedMuni === m.name;
                      const share = stateTotals.gross_revenue > 0 ? ((m.gross_revenue / stateTotals.gross_revenue) * 100) : 0;
                      return (
                        <React.Fragment key={m.name}>
                          <tr
                            data-testid={`muni-row-${idx}`}
                            className={`border-b border-slate-100 cursor-pointer transition-colors ${isExpanded ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                            onClick={() => setExpandedMuni(isExpanded ? null : m.name)}
                          >
                            <td className="py-2.5 px-3 font-medium text-slate-800">
                              <span className="inline-flex items-center gap-1">
                                {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-indigo-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                                {m.name}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-right font-medium text-emerald-700">{fc(m.gross_revenue)}</td>
                            <td className="py-2.5 px-3 text-right font-medium text-blue-700">{fc(m.net_revenue)}</td>
                            <td className="py-2.5 px-3 text-right text-slate-700">{m.scenario_count}</td>
                            <td className="py-2.5 px-3 text-slate-600 text-xs">{m.services?.join(', ') || '-'}</td>
                            <td className="py-2.5 px-3 text-center">
                              <div className="w-16 bg-slate-200 rounded-full h-1.5 mx-auto">
                                <div className="h-1.5 rounded-full bg-indigo-500" style={{ width: `${Math.min(share, 100)}%` }} />
                              </div>
                              <span className="text-[10px] text-slate-400">{share.toFixed(1)}%</span>
                            </td>
                          </tr>
                          {/* Expanded: Service Summary & Scenario Details */}
                          {isExpanded && m.scenarios?.length > 0 && (
                            <tr>
                              <td colSpan={6} className="bg-indigo-50/50 px-6 py-3">
                                {/* Service Summary Table */}
                                <div className="mb-4">
                                  <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Summary by Service</div>
                                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                          <th className="text-left py-2 px-3 font-semibold text-slate-600">Service</th>
                                          <th className="text-right py-2 px-3 font-semibold text-slate-600"># Proposals</th>
                                          <th className="text-right py-2 px-3 font-semibold text-slate-600">Gross Revenue</th>
                                          <th className="text-right py-2 px-3 font-semibold text-slate-600">Net Revenue</th>
                                          <th className="text-right py-2 px-3 font-semibold text-slate-600">Avg per Proposal</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(() => {
                                          // Aggregate scenarios by service
                                          const serviceMap = {};
                                          m.scenarios.forEach(sc => {
                                            const svc = sc.service || 'Unknown';
                                            if (!serviceMap[svc]) {
                                              serviceMap[svc] = { count: 0, gross: 0, net: 0 };
                                            }
                                            serviceMap[svc].count += 1;
                                            serviceMap[svc].gross += sc.gross_revenue || 0;
                                            serviceMap[svc].net += sc.net_revenue || 0;
                                          });
                                          return Object.entries(serviceMap)
                                            .sort((a, b) => b[1].gross - a[1].gross)
                                            .map(([svc, data]) => (
                                              <tr key={svc} className="border-b border-slate-100 last:border-0">
                                                <td className="py-2 px-3 font-medium text-slate-800">{svc}</td>
                                                <td className="py-2 px-3 text-right text-slate-700">{data.count}</td>
                                                <td className="py-2 px-3 text-right font-medium text-emerald-700">{fc(data.gross)}</td>
                                                <td className="py-2 px-3 text-right font-medium text-blue-700">{fc(data.net)}</td>
                                                <td className="py-2 px-3 text-right text-slate-600">{fc(data.gross / data.count)}</td>
                                              </tr>
                                            ));
                                        })()}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                
                                {/* Individual Proposal Details */}
                                <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Individual Proposals</div>
                                <div className="space-y-1.5">
                                  {m.scenarios.map((sc, si) => (
                                    <div key={si} className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-slate-100">
                                      <div>
                                        <span className="font-medium text-slate-800 text-sm">{sc.name}</span>
                                        <span className="ml-2 text-xs text-slate-400">{sc.service}</span>
                                      </div>
                                      <div className="flex items-center gap-4 text-xs">
                                        <span className={`px-2 py-0.5 rounded-full font-medium ${
                                          sc.status === 'approved' ? 'bg-green-100 text-green-700' :
                                          sc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                          'bg-yellow-100 text-yellow-700'
                                        }`}>{sc.status?.replace(/_/g, ' ')}</span>
                                        <span className="text-emerald-700 font-medium">{fc(sc.gross_revenue)}</span>
                                        <span className="text-blue-700 font-medium">{fc(sc.net_revenue)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {/* Totals Row */}
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                      <td className="py-2.5 px-3 text-slate-800">Total ({municipalities.length} locations)</td>
                      <td className="py-2.5 px-3 text-right text-emerald-800">{fc(stateTotals.gross_revenue)}</td>
                      <td className="py-2.5 px-3 text-right text-blue-800">{fc(stateTotals.net_revenue)}</td>
                      <td className="py-2.5 px-3 text-right text-slate-800">{stateTotals.scenario_count}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <button onClick={onClose} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors" data-testid="drilldown-close-btn">
            <ArrowLeft className="w-4 h-4" /> Back to US Map
          </button>
          <p className="text-xs text-slate-400">Data filtered by current analytics filters</p>
        </div>
      </div>
    </div>
  );
}

function MapPinOff({ className }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="2" y1="2" x2="22" y2="22"/><path d="M8.65 8.65A5 5 0 0 0 12 17c1.33-1.33 3.68-3.68 5-5"/><path d="M18 13.2A8.4 8.4 0 0 0 20 8"/>
    </svg>
  );
}
